import { DragHandleIcon } from '@chakra-ui/icons';
import { As, Box, Icon } from '@chakra-ui/react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  CSSProperties,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useSortItems } from './hooks/useSortItems';

// Row Component
export const DraggableRow = ({
  id,
  children,
  as,
}: PropsWithChildren<{ id: string; as?: As }>) => {
  const { transform, transition, setNodeRef, isDragging } = useSortable({
    id,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform), //let dnd-kit do its thing
    transition: transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1 : 0,
    position: 'relative',
  };
  return (
    // connect row ref to dnd-kit, apply important styles
    <Box ref={setNodeRef} style={style} as={as || 'div'}>
      {children}
    </Box>
  );
};

export const RowDragHandleButton = ({ rowId }: { rowId: string }) => {
  const { attributes, listeners } = useSortable({
    id: rowId,
  });
  return (
    // Alternatively, you could set these attributes on the rows themselves
    <button {...attributes} {...listeners}>
      <Icon as={DragHandleIcon} />
    </button>
  );
};

interface Row {
  id: string;
}

interface DndSortableListProps {
  data?: Row[];
  onDragStart?(event: DragStartEvent): void;
  children: (props: { sortedData: Row[] }) => React.ReactNode;
}

export default function DndSortableList({
  data = [],
  children,
  onDragStart,
}: DndSortableListProps) {
  const { onSortItems } = useSortItems({ resourceName: 'template-categories' });
  const [rows, setRows] = useState<Row[]>(data);

  const dataIds = useMemo<UniqueIdentifier[]>(
    () => (rows ? rows.map(({ id }) => id) : []),
    [rows]
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = dataIds.indexOf(active.id);
      const newIndex = dataIds.indexOf(over.id);
      const newRows = arrayMove([...rows], oldIndex, newIndex);
      setRows(newRows);
      onSortItems({ ids: newRows.map(({ id }) => id as string) });
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  useEffect(() => {
    setRows(data);
  }, [data]);

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
      onDragStart={onDragStart}
      sensors={sensors}
    >
      <SortableContext items={dataIds} strategy={verticalListSortingStrategy}>
        {children({ sortedData: rows })}
      </SortableContext>
    </DndContext>
  );
}
